import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { recordBannerClick, recordBannerImpression } from '@/api/bannerAds';

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export type AdBannerProps = {
  adId?: string;
  advertiserName: string;
  advertiserInitials: string;
  logoBackground?: string;
  logoColor?: string;
  headline: string;
  subline: string;
  ctaLabel: string;
  ctaHref: string;
  variant?: 'gold' | 'default';
  className?: string;
};

export function AdBanner({
  adId,
  advertiserName,
  advertiserInitials,
  logoBackground = '#005c34',
  logoColor = '#f2c300',
  headline,
  subline,
  ctaLabel,
  ctaHref,
  variant = 'default',
  className,
}: AdBannerProps) {
  const impressionSent = useRef(false);

  useEffect(() => {
    if (!adId || impressionSent.current) return;
    impressionSent.current = true;
    void recordBannerImpression(adId);
  }, [adId]);

  const onCtaClick = () => {
    if (adId) void recordBannerClick(adId);
  };

  const cta =
    isExternal(ctaHref) ? (
      <a href={ctaHref} target="_blank" rel="sponsored noopener noreferrer" onClick={onCtaClick}>
        <Button type="button" size="sm" variant="primary">
          {ctaLabel}
        </Button>
      </a>
    ) : (
      <Link to={ctaHref} onClick={onCtaClick}>
        <Button type="button" size="sm" variant="primary">
          {ctaLabel}
        </Button>
      </Link>
    );

  return (
    <div
      className={clsx(
        'relative flex flex-col gap-3 rounded-xl p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4',
        variant === 'gold'
          ? 'border-2 border-amber-300 bg-amber-50'
          : 'border border-slate-200 dark:border-slate-700 bg-white',
        className,
      )}
      role="complementary"
      aria-label={`Sponsored: ${advertiserName}`}
    >
      <span className="absolute right-2 top-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        Sponsored
      </span>
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-sm font-bold"
        style={{ backgroundColor: logoBackground, color: logoColor }}
        aria-hidden
      >
        {advertiserInitials}
      </div>
      <div className="min-w-0 flex-1 pr-16 sm:pr-20">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{headline}</p>
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{subline}</p>
      </div>
      <div className="shrink-0 sm:ml-auto">{cta}</div>
    </div>
  );
}
